import axios from "axios";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../Features/config/firebase";
import { isAuthApiError, redirectToLogin } from "../utils/authRedirect";

const AUTH_WAIT_MS = 4000;

function isPublicAuthRoute(url?: string): boolean {
  return url === "/user/login" || Boolean(url?.startsWith("/user/signup"));
}

/** Wait briefly for Firebase to restore session on cold load before failing requests. */
function waitForFirebaseUser(timeoutMs = AUTH_WAIT_MS): Promise<typeof auth.currentUser> {
  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }
  return new Promise((resolve) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        unsub();
        resolve(null);
      }
    }, timeoutMs);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      unsub();
      resolve(user);
    });
  });
}

// Create an Axios instance if you don't already have one (recommended)
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL || "http://localhost:8080",

  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add the token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const user = auth.currentUser ?? (await waitForFirebaseUser());
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      } else if (!isPublicAuthRoute(config.url)) {
        await redirectToLogin();
        return Promise.reject(new Error("User not authenticated. Please login."));
      }
    } catch (error) {
      if (isAuthApiError(error)) {
        await redirectToLogin();
      }
      console.error("token error", error);
      return Promise.reject(error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — redirect to login on auth failures, then return path after sign-in
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isAuthApiError(error)) {
      await redirectToLogin();
    }
    return Promise.reject(error);
  }
);

export default apiClient; // Export the configured Axios instance
